import styled, { css } from 'styled-components';
import { ANIMATION_CURVE, FOCUS_OUTLINE_STYLE, getSpacingToken, getToken } from '@optimus-web/common';
import { Icon } from '../Icon';

export const NativeControlElement = styled.input.attrs({
    type: 'checkbox',
})`
    width: ${getToken('sizing550')}px;
    height: ${getToken('sizing300')}px;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    position: absolute;
    padding: ${getSpacingToken('0')}px;
    margin: ${getSpacingToken('0')}px;
    top: 0;
    left: 0;
    box-sizing: border-box;
    opacity: 0;
    z-index: 2;
`;

interface ToggleBackgroundElementProps {
    checked?: boolean;
    disabled?: boolean;
}

export const ToggleBackgroundElement = styled.div<ToggleBackgroundElementProps>`
    display: flex;
    cursor: pointer;
    border-radius: ${getSpacingToken('150')}px;
    height: ${getToken('sizing300')}px;
    outline: none;
    position: relative;
    width: ${getToken('sizing550')}px;
    user-select: none;

    &:before {
        display: block;
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        border-radius: ${getSpacingToken('150')}px;
        content: '';
        background-color: ${({ checked }) =>
            checked
                ? getToken('backgroundInteractivePrimaryDefault')
                : getToken('backgroundInteractiveNeutralBoldDefault')};

        ${({ disabled }) =>
            disabled &&
            css`
                background-color: ${getToken('backgroundDisabled')};
            `}

        transition-duration: 150ms;
        transition-property: opacity, background-color;
        transition-timing-function: ${ANIMATION_CURVE.default};
    }
`;

export const ToggleWrapperElement = styled.div<ToggleBackgroundElementProps>`
    display: inline-block;
    position: relative;
    vertical-align: middle;
    line-height: initial;

    &:hover {
        ${ToggleBackgroundElement} {
            &:before {
                ${({ checked, disabled }) =>
                    !disabled &&
                    css`
                        background-color: ${checked
                            ? getToken('backgroundInteractivePrimaryHover')
                            : getToken('backgroundInteractiveNeutralBoldHover')};
                    `}
            }
        }
    }

    ${NativeControlElement} {
        ${({ disabled, checked }) =>
            !disabled &&
            css`
                &:focus-visible {
                    & + ${ToggleBackgroundElement} {
                        ${FOCUS_OUTLINE_STYLE}
                    }
                }

                &:active {
                    & + ${ToggleBackgroundElement} {
                        &:before {
                            background-color: ${checked
                                ? getToken('backgroundInteractivePrimaryActive')
                                : getToken('backgroundInteractiveNeutralBoldActive')};
                        }
                    }
                }
            `}
    }
`;

export interface ToggleKnobElementProps {
    checked?: boolean;
}

export const ToggleKnobElement = styled.div<ToggleKnobElementProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${getToken('borderRadiusRound')};
    height: ${getToken('sizing200')}px;
    width: ${getToken('sizing200')}px;
    position: absolute;
    top: ${getSpacingToken('50')}px;
    left: ${({ checked }) => (checked ? getSpacingToken('25') : getSpacingToken('50'))}px;
    transition-duration: 90ms;
    transition-property: transform;
    transition-timing-function: ${ANIMATION_CURVE.default};
    background-color: ${getToken('backgroundStaticFlat')};
    transform: translateX(0);
    z-index: 1;
    ${({ checked }) =>
        checked &&
        css`
            transform: translateX(calc(${getSpacingToken('300')}px - ${getSpacingToken('25')}px));
        `}
`;

export const IconElement = styled(Icon).attrs({
    size: 'small',
})<{ disabled?: boolean }>`
    position: relative;
    margin-left: auto;
    margin-right: 6px;
    align-self: center;
    font-size: ${getToken('fontSize200')}px;
    color: ${getToken('textStaticInverse')};
    ${({ disabled }) =>
        disabled &&
        css`
            color: ${getToken('textDisabled')};
        `}
`;

export const CheckedIconElement = styled(IconElement)`
    margin-left: 6px;
    margin-right: unset;
    ${({ disabled }) =>
        disabled &&
        css`
            color: ${getToken('textDisabled')};
        `}
`;
